import React from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import { DEFAULT_QUERY_OPTIONS } from '../../../../support/constants';
import { apiGet, apiPutJson } from '../../../../support/aiApi';
import { normalizeItems, getTotalCount } from '../tuning/TuningAdminTable';
import { dateLabel, statusChip } from './ragAdminUtils';

type SlotName = 'blue' | 'green';

function valueOf(...values: any[]) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }

  return null;
}

function idOf(value: any): number | string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' || typeof value === 'string') return value;

  return value.id ?? value.Id ?? value.pk ?? value.value ?? null;
}

function normalizeSlot(value?: any): SlotName {
  const slot = String(value || '').toLowerCase();

  if (slot === 'blue') return 'blue';
  if (slot === 'green') return 'green';

  return 'blue';
}

function oppositeSlot(slot?: any): SlotName {
  return normalizeSlot(slot) === 'blue' ? 'green' : 'blue';
}

function pipelineObjectForSlot(group: any, slot: SlotName) {
  if (slot === 'blue') {
    return valueOf(
      group?.blue_pipeline_detail,
      group?.blue_pipeline,
      group?.blue,
    );
  }

  return valueOf(
    group?.green_pipeline_detail,
    group?.green_pipeline,
    group?.green,
  );
}

function activeSlotForGroup(group: any): SlotName {
  return normalizeSlot(group?.active_slot);
}

/**
 * Ingest is intentionally inferred from active slot.
 *
 * The profile has one active read slot. The standby/non-active slot is the
 * ingest/write slot. This prevents the UI from reporting active/ingest states
 * that drift apart.
 */
function ingestSlotForGroup(group: any): SlotName {
  return oppositeSlot(activeSlotForGroup(group));
}

function activePipelineForGroup(group: any) {
  return pipelineObjectForSlot(group, activeSlotForGroup(group));
}

function ingestPipelineForGroup(group: any) {
  return pipelineObjectForSlot(group, ingestSlotForGroup(group));
}

function pipelineName(pipeline: any, fallback = '-') {
  if (!pipeline) return fallback;

  if (typeof pipeline === 'string' || typeof pipeline === 'number') {
    return `Pipeline ${pipeline}`;
  }

  return (
    pipeline.name ||
    pipeline.display_name ||
    pipeline.key ||
    pipeline.provider_pipeline_id ||
    pipeline.gcs_bucket ||
    pipeline.pubsub_topic ||
    `Pipeline ${pipeline.id || fallback}`
  );
}

function pipelineExternalId(pipeline: any) {
  if (!pipeline) return '-';

  if (typeof pipeline === 'string' || typeof pipeline === 'number') {
    return String(pipeline);
  }

  return (
    pipeline.provider_pipeline_id ||
    pipeline.pipeline_id ||
    pipeline.external_id ||
    pipeline.gcs_bucket ||
    pipeline.pubsub_topic ||
    pipeline.id ||
    '-'
  );
}

function pipelineStatusValue(pipeline: any) {
  if (!pipeline || typeof pipeline !== 'object') return 'unknown';
  return pipeline.status || 'unknown';
}

function slotColor(slot?: string | null): 'success' | 'warning' | 'info' | 'default' {
  const value = String(slot || '').toLowerCase();

  if (value === 'blue') return 'info';
  if (value === 'green') return 'success';
  if (value) return 'warning';

  return 'default';
}

function buildPipelineGroupPayload(group: any, activeSlot: SlotName) {
  const ingestSlot = oppositeSlot(activeSlot);

  return {
    id: group.id,
    key: group.key,
    name: group.name,
    description: group.description || '',
    provider: group.provider || 'drift',

    blue_pipeline: valueOf(
      group.blue_pipeline_id,
      idOf(group.blue_pipeline),
      idOf(group.blue_pipeline_detail),
    ),
    green_pipeline: valueOf(
      group.green_pipeline_id,
      idOf(group.green_pipeline),
      idOf(group.green_pipeline_detail),
    ),

    /**
     * Keep both fields updated for backend compatibility, but the UI treats
     * ingest_slot as derived from active_slot.
     */
    active_slot: activeSlot,
    ingest_slot: ingestSlot,

    enabled: group.enabled ?? group.is_active ?? true,
    is_active: group.is_active ?? group.enabled ?? true,
    updated_by: 'admin-ui',
  };
}

const PipelineMiniCard: React.FC<{
  title: string;
  slot: SlotName;
  pipeline: any;
  activeSlot: SlotName;
  ingestSlot: SlotName;
  onSetActive: () => void;
  disabled?: boolean;
}> = ({
        title,
        slot,
        pipeline,
        activeSlot,
        ingestSlot,
        onSetActive,
        disabled,
      }) => {
  const isActiveSlot = activeSlot === slot;
  const isStandbySlot = !isActiveSlot;
  const isIngestSlot = ingestSlot === slot;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        borderColor: isActiveSlot ? 'success.main' : 'warning.main',
        bgcolor: isActiveSlot ? 'success.50' : 'warning.50',
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>

          <Chip size="small" variant="outlined" color={slotColor(slot)} label={slot} />

          {isActiveSlot ? (
            <Chip size="small" color="success" variant="filled" label="Profile active read slot" />
          ) : null}

          {isStandbySlot ? (
            <Chip size="small" color="warning" variant="outlined" label="Standby / inferred ingest slot" />
          ) : null}

          {isIngestSlot && !isActiveSlot ? (
            <Chip size="small" color="warning" variant="outlined" label="Ingest inferred" />
          ) : null}
        </Stack>

        <Box>
          <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
            {pipelineName(pipeline)}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ wordBreak: 'break-word' }}
          >
            {pipelineExternalId(pipeline)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Concrete pipeline provider status
          </Typography>
          {statusChip(pipelineStatusValue(pipeline))}
        </Box>

        <Box>
          {isActiveSlot ? (
            <Typography variant="body2" color="text.secondary">
              This slot is currently active for runtime retrieval.
            </Typography>
          ) : (
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={disabled}
              startIcon={<SwapHorizIcon fontSize="small" />}
              onClick={onSetActive}
            >
              Set Active
            </Button>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

const RagPipelineProfilesPanel: React.FC = () => {
  const queryClient = useQueryClient();

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [selectedGroupId, setSelectedGroupId] = React.useState<number | string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const query = useQuery<any>({
    queryKey: [
      'rag-pipeline-groups',
      'profile-slots',
      {
        page,
        pageSize,
      },
    ],
    queryFn: () =>
      apiGet<any>('db/rag-pipeline-group/', {
        page: page + 1,
        size: pageSize,
        detailed: true,
        depth: 2,
        'order-by': 'name',
      }),
    ...DEFAULT_QUERY_OPTIONS,
    placeholderData: keepPreviousData,
  });

  const groups = normalizeItems(query.data);
  const totalCount = getTotalCount(query.data, groups.length);

  React.useEffect(() => {
    if (selectedGroupId || !groups.length) return;
    setSelectedGroupId(groups[0].id);
  }, [groups, selectedGroupId]);

  const selectedGroup =
    groups.find((group) => String(group.id) === String(selectedGroupId)) || null;

  const updateGroupMutation = useMutation<any, Error, { group: any; activeSlot: SlotName }>({
    mutationFn: async ({ group, activeSlot }) => {
      const payload = buildPipelineGroupPayload(group, activeSlot);
      return apiPutJson<any>(`db/rag-pipeline-group/${group.id}/`, payload);
    },
    onMutate: () => {
      setError(null);
    },
    onSuccess: async (saved) => {
      if (saved?.id) {
        setSelectedGroupId(saved.id);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['rag-pipeline-groups'] }),
        queryClient.invalidateQueries({ queryKey: ['rag-overview'] }),
        queryClient.invalidateQueries({ queryKey: ['rag-product-coverage'] }),
        queryClient.invalidateQueries({ queryKey: ['product-rag-pipeline-mapping'] }),
        queryClient.invalidateQueries({ queryKey: ['rag-routing-mapping'] }),
      ]);

      await query.refetch();
    },
    onError: (err: any) => {
      setError(err?.message || 'Failed to update active pipeline profile slot.');
    },
  });

  const refresh = async () => {
    setError(null);
    await query.refetch();
  };

  const setActiveSlot = async (group: any, nextActiveSlot: SlotName) => {
    if (!group?.id) return;

    const currentActiveSlot = activeSlotForGroup(group);

    if (currentActiveSlot === nextActiveSlot) return;

    const nextIngestSlot = oppositeSlot(nextActiveSlot);
    const nextPipeline = pipelineObjectForSlot(group, nextActiveSlot);
    const currentPipeline = pipelineObjectForSlot(group, currentActiveSlot);

    const confirmed = window.confirm(
      [
        `Set ${nextActiveSlot.toUpperCase()} as the active runtime slot for "${group.name || group.key}"?`,
        '',
        `New active pipeline: ${pipelineName(nextPipeline)} (${pipelineExternalId(nextPipeline)})`,
        `Previous active pipeline: ${pipelineName(currentPipeline)} (${pipelineExternalId(currentPipeline)})`,
        `Ingest slot will be inferred as ${nextIngestSlot.toUpperCase()}.`,
      ].join('\n'),
    );

    if (!confirmed) return;

    await updateGroupMutation.mutateAsync({
      group,
      activeSlot: nextActiveSlot,
    });
  };

  const saving = updateGroupMutation.isPending;

  const selectedActiveSlot = selectedGroup ? activeSlotForGroup(selectedGroup) : 'blue';
  const selectedIngestSlot = selectedGroup ? ingestSlotForGroup(selectedGroup) : 'green';

  const activePipeline = selectedGroup ? activePipelineForGroup(selectedGroup) : null;
  const ingestPipeline = selectedGroup ? ingestPipelineForGroup(selectedGroup) : null;
  const bluePipeline = selectedGroup ? pipelineObjectForSlot(selectedGroup, 'blue') : null;
  const greenPipeline = selectedGroup ? pipelineObjectForSlot(selectedGroup, 'green') : null;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              RAG Pipeline Profiles and Blue/Green Slots
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Manage profile-level active slots. Runtime retrieval reads from the active slot.
              The ingest/write slot is inferred as the non-active standby pipeline.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<RefreshIcon />}
              variant="outlined"
              onClick={refresh}
              disabled={query.isLoading || saving}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {query.isLoading && !groups.length ? <LinearProgress /> : null}

      {query.isFetching && groups.length ? (
        <Chip
          size="small"
          variant="outlined"
          label="Refreshing profile state..."
          sx={{ alignSelf: 'flex-start' }}
        />
      ) : null}

      {query.error ? (
        <Alert severity="error">
          {(query.error as Error)?.message || 'Failed to load RAG pipeline profiles.'}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} xl={5}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" fontWeight={700}>
                Profiles
              </Typography>

              <Chip size="small" variant="outlined" label={`${totalCount} total`} />
            </Stack>

            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Profile</TableCell>
                    <TableCell width={110}>Provider</TableCell>
                    <TableCell width={180}>Slots</TableCell>
                    <TableCell>Active Pipeline</TableCell>
                    <TableCell width={100}>Enabled</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {groups.map((group) => {
                    const selected = String(selectedGroupId) === String(group.id);
                    const activeSlot = activeSlotForGroup(group);
                    const inferredIngestSlot = ingestSlotForGroup(group);
                    const active = activePipelineForGroup(group);

                    return (
                      <TableRow
                        key={String(group.id)}
                        hover
                        selected={selected}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedGroupId(group.id)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                            {group.name || group.key}
                          </Typography>

                          <Typography variant="caption" color="text.secondary" display="block">
                            {group.key || `ID ${group.id}`}
                          </Typography>

                          {group.description ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ wordBreak: 'break-word' }}
                            >
                              {group.description}
                            </Typography>
                          ) : null}
                        </TableCell>

                        <TableCell>
                          <Chip size="small" variant="outlined" label={group.provider || '-'} />
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.5}>
                            <Chip
                              size="small"
                              color={slotColor(activeSlot)}
                              variant="outlined"
                              label={`Active: ${activeSlot}`}
                            />

                            <Chip
                              size="small"
                              color={slotColor(inferredIngestSlot)}
                              variant="outlined"
                              label={`Ingest: ${inferredIngestSlot} inferred`}
                            />
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                            {pipelineName(active)}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ wordBreak: 'break-word' }}
                          >
                            {pipelineExternalId(active)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            color={group.enabled !== false && group.is_active !== false ? 'success' : 'default'}
                            variant="outlined"
                            label={group.enabled !== false && group.is_active !== false ? 'yes' : 'no'}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {!groups.length && !query.isLoading ? (
              <Typography variant="body2" color="text.secondary" mt={2}>
                No RAG pipeline profiles found.
              </Typography>
            ) : null}

            <TablePagination
              component="div"
              count={Number(totalCount || 0)}
              page={page}
              rowsPerPage={pageSize}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} xl={7}>
          {!selectedGroup ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Select a profile to inspect and manage blue/green active slot state.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', md: 'flex-start' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="h6" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                        {selectedGroup.name || selectedGroup.key}
                      </Typography>

                      <Chip size="small" variant="outlined" label={selectedGroup.key || `ID ${selectedGroup.id}`} />
                      <Chip size="small" variant="outlined" label={`Provider: ${selectedGroup.provider || '-'}`} />

                      <Chip
                        size="small"
                        color={slotColor(selectedActiveSlot)}
                        variant="outlined"
                        label={`Active: ${selectedActiveSlot}`}
                      />

                      <Chip
                        size="small"
                        color={slotColor(selectedIngestSlot)}
                        variant="outlined"
                        label={`Ingest: ${selectedIngestSlot} inferred`}
                      />
                    </Stack>

                    {selectedGroup.description ? (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {selectedGroup.description}
                      </Typography>
                    ) : null}

                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                      Updated: {dateLabel(selectedGroup.last_updated || selectedGroup.updated || selectedGroup.created)}
                    </Typography>
                  </Box>

                  {saving ? (
                    <Chip size="small" color="warning" variant="outlined" label="Saving..." />
                  ) : null}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Effective Routing
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                        <Typography variant="caption" color="text.secondary">
                          Runtime Active Pipeline
                        </Typography>

                        <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                          {pipelineName(activePipeline)}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ wordBreak: 'break-word' }}
                        >
                          {pipelineExternalId(activePipeline)}
                        </Typography>

                        <Box mt={1}>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Concrete pipeline provider status
                          </Typography>
                          {statusChip(pipelineStatusValue(activePipeline))}
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                        <Typography variant="caption" color="text.secondary">
                          Standby / Inferred Ingest Pipeline
                        </Typography>

                        <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                          {pipelineName(ingestPipeline)}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ wordBreak: 'break-word' }}
                        >
                          {pipelineExternalId(ingestPipeline)}
                        </Typography>

                        <Box mt={1}>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Concrete pipeline provider status
                          </Typography>
                          {statusChip(pipelineStatusValue(ingestPipeline))}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <PipelineMiniCard
                    title="Blue Pipeline"
                    slot="blue"
                    pipeline={bluePipeline}
                    activeSlot={selectedActiveSlot}
                    ingestSlot={selectedIngestSlot}
                    disabled={saving}
                    onSetActive={() => setActiveSlot(selectedGroup, 'blue')}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <PipelineMiniCard
                    title="Green Pipeline"
                    slot="green"
                    pipeline={greenPipeline}
                    activeSlot={selectedActiveSlot}
                    ingestSlot={selectedIngestSlot}
                    disabled={saving}
                    onSetActive={() => setActiveSlot(selectedGroup, 'green')}
                  />
                </Grid>
              </Grid>

              <Alert severity="info" icon={<SwapHorizIcon />}>
                The only editable routing control is the profile active slot. The standby slot is automatically inferred
                as the ingest/write slot. Clicking <b>Set Active</b> on the standby pipeline promotes it for future
                runtime RAG retrieval and moves the previous active pipeline into standby/ingest.
              </Alert>
            </Stack>
          )}
        </Grid>
      </Grid>
    </Stack>
  );
};

export default RagPipelineProfilesPanel;